import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { stroot, organisationUsers, user as userAPI } from '@birdi/js-sdk';
import { connect } from 'react-redux';
import * as userActions from '../pilot-settings/pilot-profile/actions';
import { css } from '@emotion/react';
import { useMetaSelector } from '../meta/reducer';
import {
  BirdiTheme,
  buttonBlue,
  breakpoints,
  blocks,
  forms,
} from '@birdi/theme';
import { format, parseISO } from 'date-fns';
import { useForm, Controller } from 'react-hook-form';
import { validatePhoneNumber } from '../onboarding/utils';
import * as orgUserActions from '../administration/users/reducer';
import UploadAvatar from './upload-avatar';
import * as Toast from '@radix-ui/react-toast';
import * as styles from '../administration/styles';
import HandsSVG from '@birdi/icons/thank-you.svg';
import {
  LocationSelect,
  blankLocationWithName,
  blacklistedAreas,
} from '@birdi/google-places-autocomplete';
import { LoadingSpinner } from '@birdi/loading';

interface GeneralInternalProps {
  account: any;
  updateStateAndPatch: (field: any) => void;
  updateAvatarAndPatch: (field: any) => void;
  removeUser: (id: string) => void;
}

const GeneralInternal = (props: GeneralInternalProps) => {
  const { updateStateAndPatch, updateAvatarAndPatch, removeUser } = props;
  const params = useParams<{ scopeId: string }>();

  const navigate = useNavigate();

  const meta = useMetaSelector();
  // if (meta.scopeId === params.scopeId) => myself
  const mySelf = meta.scopeId === params.scopeId;

  const { register, handleSubmit, reset, control, formState, watch } =
    useForm();

  const [details, setDetails] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [toastOpen, setToastOpen] = React.useState(false);

  const [updateLoading, setUpdateLoading] = useState(false);
  const [locationHasChanged, setLocationHasChanged] = useState(false);

  const setData = (response) => {
    // console.log('response: ', response);
    setDetails(response);
    const initialData = {
      fname: response.fname,
      lname: response.lname,
      phone: response.phone,
      email: response.email,
      company: response.company,
      position: response.position,
      location: response.location,
    };
    reset(initialData);
    setInitialValues(initialData);
  };

  useEffect(() => {
    // if (mySelf) {
    //   organisationUsers.getMyDetails(stroot('if9eey'))
    //     .then((res) => setData(res.body));
    // }
    organisationUsers
      .getUserDetails(stroot('as7oo7'), params.scopeId)
      .then((res) => setData(res.body));
  }, [meta.loaded, mySelf, params.scopeId]);

  const currentValues = watch(); // Monitor form values

  const hasChanges = Object.keys(initialValues).some((key) => {
    const initial = initialValues[key];
    const current = currentValues[key];

    if (key === 'location') return initial.name !== current.name;
    return initial !== current;
  });

  const finishSave = () => {
    setInitialValues(currentValues);
    setDetails({ ...details, ...currentValues });
    setToastOpen(true);
    window.setTimeout(() => setToastOpen(false), 2000);
    setUpdateLoading(false);
  };

  const save = async () => {
    const initialValuesExceptLocation = initialValues;
    delete initialValuesExceptLocation.location;

    const valuesToUpdate = Object.keys(initialValuesExceptLocation).reduce(
      (acc, key) => {
        if (currentValues[key] !== initialValuesExceptLocation[key])
          acc[key] = currentValues[key];
        return acc;
      },
      {},
    );
    if (!Object.keys(valuesToUpdate).length && !locationHasChanged)
      return false;

    setUpdateLoading(true);

    if (locationHasChanged) {
      await userAPI.updateUserLocation(
        stroot('gu2ohm'),
        params.scopeId,
        currentValues.location,
      );
      setLocationHasChanged(false);
    }

    if (Object.keys(valuesToUpdate).length) {
      if (mySelf) {
        await updateStateAndPatch(valuesToUpdate);
      } else {
        await userAPI.update(stroot('ohp4om'), params.scopeId, valuesToUpdate);
      }
    }

    finishSave();
  };

  if (!details) return <div />;

  // const deleteUser = async (userId: string) => {
  //   await removeUser(userId);
  //   history.push('/admin-users')
  // };

  const deleteUser = (userId: string, workspaceId: string) => {
    organisationUsers
      .deleteUserFromWorkspace(stroot('shie9a'), userId, workspaceId)
      .then(() => {
        navigate('/admin-users');
        // if we still need the users reducer
        // if (workspaceId === meta.organisation.id) {
        //   dispatch(userActions.getUsers());
        // }
      });
  };

  return (
    <div>
      <div css={blocks.pageTitleHolder}>
        <h2 css={blocks.pageTitle}>
          {details.fname} {details.lname}
          <span
            css={css`
              font-weight: normal;
            `}
          >
            &nbsp;&nbsp;&nbsp;
            {details.roleName}
          </span>
        </h2>
      </div>
      <div
        css={(theme) => css`
          ${blocks.pageGraySection(theme)};
          justify-content: space-between;
          position: relative;
          @media (max-width: ${breakpoints.xs}) {
            padding-bottom: 1em;
            p {
              margin-bottom: 0;
            }
          }
        `}
      >
        <p>Manage users account information.</p>
        {/* {!mySelf && (details.roleName !== 'Creator') && (
          <div
            css={css`
              @media (max-width: ${breakpoints.xs}) {
                width: 100%;
                display: flex;
                justify-content: flex-end;
              }
            `}
          >
            <button
              type="button"
              onClick={() => {
                confirm('Are you sure you want to delete this user?') &&
                  deleteUser(details.id, details.organisationId);
              }}
              css={(theme) => css`
                ${buttonOutlineBlack(theme)};
                background-color: ${theme.mono0};
                border: 1px solid ${theme.mono40};
                border-radius: 4px;
                height: 2em;
                span {
                  color: ${theme.mono50};
                }
              `}
            >
              <span>Delete user</span>
            </button>
          </div>
        )} */}
      </div>

      <div
        css={css`
          padding: 1em;
        `}
      >
        <div
          css={(theme: BirdiTheme) => css`
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            gap: 3em;
            padding: 1em 3em;
            border: solid 1px ${theme.mono20};
            border-radius: 5px;
            width: 80%;
            @media (max-width: ${breakpoints.xs}) {
              width: calc(100% - 2em);
              gap: 1em;
              padding: 1em;
            }
          `}
        >
          <UploadAvatar
            scopeId={params.scopeId}
            mySelf={mySelf}
            details={details}
            updateAvatarAndPatch={updateAvatarAndPatch}
          />
          <div
            css={css`
              span {
                display: block;
              }
            `}
          >
            <span
              css={css`
                font-size: 1.1em;
                font-weight: bold;
              `}
            >
              {`${details.fname || '-'} ${details.lname || '-'}`}
            </span>
            <span>{details.email}</span>
            <span>{`Phone: ${details.phone || '-'}`}</span>
            <span>{`Job Title: ${details.position || '-'}`}</span>
            <span>{`Location: ${details.locationId ? `${details.location?.suburb}, ${details.location?.state}` : '-'}`}</span>
            <span>{`Member Since: ${format(parseISO(details.createdAt), 'dd MMM yyyy')}`}</span>
            <span>Last Active: -</span>
            <span>{`Workspace Name: ${details.organisationName}`}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit(save)}>
          <div
            css={css`
              margin-top: 2em;
              display: flex;
              flex-direction: row;
              justify-content: flex-start;
              align-items: flex-start;
              width: calc(100% - 3em);
              gap: 1em;
              @media (max-width: ${breakpoints.xs}) {
                flex-direction: column;
                gap: 0;
                width: 100%;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                flex: 1;
                flex-direction: column;
                width: 100%;
              `}
            >
              <label css={forms.blockLabel} htmlFor="company">
                Company Name
                <input
                  css={forms.textInput}
                  type="text"
                  {...register('company')}
                />
              </label>
              <label css={forms.blockLabel} htmlFor="fname">
                First Name
                <input
                  css={forms.textInput}
                  type="text"
                  id="fname"
                  {...register('fname')}
                />
              </label>
              <label css={forms.blockLabel} htmlFor="lname">
                Last Name
                <input
                  css={forms.textInput}
                  type="text"
                  id="lname"
                  {...register('lname')}
                />
              </label>
              <label css={forms.blockLabel} htmlFor="email">
                Email
                <input
                  css={forms.textInput}
                  type="text"
                  id="email"
                  {...register('email')}
                  disabled
                />
              </label>
            </div>
            <div
              css={css`
                display: flex;
                flex: 1;
                flex-direction: column;
                width: 100%;
              `}
            >
              <label css={forms.blockLabel} htmlFor="position">
                Job Title
                <input
                  css={forms.textInput}
                  type="text"
                  {...register('position')}
                />
              </label>
              <label css={forms.blockLabel} htmlFor="phone">
                Phone
                <Controller
                  render={({ field: { onChange } }) => (
                    <input
                      css={forms.textInput}
                      type="text"
                      id="phone"
                      onChange={(event) => {
                        onChange(event.target.value);
                      }}
                      defaultValue={details.phone}
                    />
                  )}
                  control={control}
                  name="phone"
                  rules={{
                    validate: (value: string) =>
                      value !== null && validatePhoneNumber(value),
                  }}
                />
                {formState.errors.phone && (
                  <span css={forms.errorMessage}>
                    Please provide a valid mobile number.
                  </span>
                )}
              </label>
              <label css={forms.blockLabel} htmlFor="location">
                Location
                <Controller
                  control={control}
                  name="location"
                  {...register('location')}
                  render={({ field: { onChange } }) => (
                    <LocationSelect
                      inputId="location"
                      defaultValue={details?.location?.name}
                      isClearable
                      onClear={() => {
                        onChange(blankLocationWithName);
                        setLocationHasChanged(true);
                      }}
                      onLocationSelect={(geocode) => {
                        onChange(geocode.location);
                        setLocationHasChanged(true);
                      }}
                      placeholder="Look up suburbs"
                      styles={forms.reactSelect}
                    />
                  )}
                />
              </label>
              <div
                css={css`
                  display: flex;
                  justify-content: flex-end;
                `}
              >
                <Toast.Provider>
                  <button
                    type="submit"
                    css={(theme) => css`
                      ${buttonBlue(theme)};
                      height: 2.5em;
                      gap: 0.5em;
                    `}
                    disabled={!hasChanges}
                  >
                    Save changes
                    {updateLoading && <LoadingSpinner />}
                  </button>
                  <Toast.Root css={styles.toastRoot} open={toastOpen}>
                    <Toast.Description css={styles.toastContent}>
                      <HandsSVG />
                      Profile data updated
                    </Toast.Description>
                  </Toast.Root>
                  <Toast.Viewport css={styles.toastViewport} />
                </Toast.Provider>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export const General = connect(
  (state) => ({
    account: state.account,
  }),
  (dispatch) => ({
    updateStateAndPatch: (field) =>
      dispatch(userActions.updateStateAndPatch(field)),
    updateAvatarAndPatch: (field) =>
      dispatch(userActions.updateAvatarAndPatch(field)),
    removeUser: (id) => dispatch(orgUserActions.remove(id)),
  }),
)(GeneralInternal);
